// src/pages/Contacts/index.js — tela de Contatos no padrão Aeros Dark
// (mockup "Tela Aeros Dark - Contatos e Modais"): header com busca/Tags/
// Importar-Exportar/Novo contato, barra de seleção, tabela em CSS grid com
// coluna de Tags, paginação numerada e modais Aeros (contato, atendimento,
// importação por planilha, agenda do WhatsApp, bloquear/excluir).
import React, { useState, useEffect, useReducer, useContext, useMemo } from "react";
import { toast } from "react-toastify";
import { useHistory, useLocation } from "react-router-dom";
import { utils as xlsxUtils, writeFile as xlsxWriteFile } from "xlsx";

import { makeStyles, alpha, useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Avatar from "@material-ui/core/Avatar";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import { Facebook, Instagram, WhatsApp, Close, Sms } from "@material-ui/icons";
import SearchIcon from "@material-ui/icons/Search";
import InputBase from "@material-ui/core/InputBase";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import DeleteSweepIcon from "@material-ui/icons/DeleteSweep";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import BlockIcon from "@material-ui/icons/Block";
import AddIcon from "@material-ui/icons/Add";
import LocalOfferOutlinedIcon from "@material-ui/icons/LocalOfferOutlined";
import PersonPinOutlinedIcon from "@material-ui/icons/PersonPinCircleOutlined";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import ForumOutlinedIcon from "@material-ui/icons/ForumOutlined";
import GetAppIcon from "@material-ui/icons/GetApp";
import Checkbox from "@material-ui/core/Checkbox";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import Popover from "@material-ui/core/Popover";
import CircularProgress from "@material-ui/core/CircularProgress";
import Alert from "@material-ui/lab/Alert";
import AlertTitle from "@material-ui/lab/AlertTitle";

import api from "../../services/api";
import { AEROS, stateText } from "../../styles/aerosTokens";
import {
  avatarColorFor,
  initialsOf,
  maskPhone,
  walletNameOf,
  tagChip,
  exportRow,
  totalPages,
  pageWindow,
} from "./contactRules";
import ContactModal from "../../components/ContactModal";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import ContactImportModal from "../../components/ContactImportModal";
import ContactImportPhoneModal from "../../components/ContactImportPhoneModal";
import { BulkTagPopover, BulkWalletPopover } from "../../components/ContactBulkPopovers";
import ImportacaoProgresso from "../../components/ImportacaoProgresso";
import { i18n } from "../../translate/i18n";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import NewTicketModal from "../../components/NewTicketModal";
import { TagsFilter } from "../../components/TagsFilter";
import formatSerializedId from "../../utils/formatSerializedId";
import { v4 as uuidv4 } from "uuid";
import EnviarSmsModal from "../../components/EnviarSmsModal";
import useCompanySettings from "../../hooks/useSettings/companySettings";
import { TicketsContext } from "../../context/Tickets/TicketsContext";

// GET /contacts aceita pageSize (1..200); a tela do mockup pagina de 20.
const PAGE_SIZE = 20;

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "BULK_DELETE_CONTACTS") {
    const contactIds = action.payload;
    return state.filter(contact => !contactIds.includes(contact.id));
  }

  if (action.type === "DELETE_ALL_CONTACTS") {
    const { excludeIds = [] } = action.payload;
    return state.filter(contact => excludeIds.includes(contact.id));
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "SET_TOTAL_COUNT") {
    // Não altera o estado, mas permite rastrear o total
    return state;
  }
};



// Grid da tabela — espelha o mockup Contatos A:
// [ ] | Nome (+#id) | WhatsApp | E-mail | Tags | Status | Carteira | Ações
const TABLE_GRID = "48px 1.9fr 1.3fr 1.6fr 1.4fr 0.9fr 1.1fr 1.5fr";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderStrong = isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.14)";
  const cyanText = stateText(theme, AEROS.cyan, "info");
  const purpleText = stateText(theme, "#C08BFF", "purple");
  const errorText = stateText(theme, AEROS.error, "error");
  return {
    // ---- header da página (60px, título + contagem, ações à direita) ----
    pageHeader: {
      minHeight: 60,
      flexShrink: 0,
      display: "flex",
      alignItems: "center",
      gap: 16,
      padding: "0 28px",
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.06)"}`,
      [theme.breakpoints.down("sm")]: { padding: "8px 12px", flexWrap: "wrap" },
    },
    pageTitle: {
      fontFamily: AEROS.fontDisplay,
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      color: theme.palette.text.primary,
      lineHeight: 1.3,
    },
    pageTitleCount: { color: theme.palette.text.secondary, fontWeight: 500 },
    pageSubtitle: { fontSize: 12, color: theme.palette.text.secondary, lineHeight: 1.3 },
    headerActions: { marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
    searchPill: {
      width: 220,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"}`,
      borderRadius: 10,
      padding: "5px 14px",
      fontSize: 13,
      color: theme.palette.text.primary,
      display: "flex",
      alignItems: "center",
      gap: 8,
      "& .MuiSvgIcon-root": { fontSize: 18, color: theme.palette.text.secondary },
      "& input::placeholder": { color: theme.palette.text.secondary, opacity: 1 },
    },
    ghostButton: {
      border: `1px solid ${borderStrong}`,
      borderRadius: 10,
      padding: "7px 14px",
      fontSize: 13,
      fontWeight: 600,
      color: theme.palette.text.primary,
      "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 17 },
      "& .MuiButton-endIcon .MuiSvgIcon-root": { fontSize: 17 },
    },
    ghostButtonActive: { borderColor: alpha(AEROS.cyan, 0.5), backgroundColor: alpha(AEROS.cyan, 0.06), color: cyanText },
    tagsCount: {
      marginLeft: 6,
      minWidth: 18,
      height: 18,
      padding: "0 6px",
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 700,
      lineHeight: "18px",
      backgroundColor: "rgba(160,68,255,0.2)",
      color: purpleText,
    },
    tagsPopover: {
      width: 320,
      padding: 4,
      backgroundColor: theme.palette.aeros.surface2,
      borderRadius: 12,
      "& .MuiBox-root": { padding: 8 },
    },
    gradientButton: {
      borderRadius: 10,
      padding: "7px 16px",
      fontSize: 13,
      fontWeight: 700,
      "& .MuiSvgIcon-root": { fontSize: 17 },
    },
    // ---- menu Importar/Exportar ----
    menuPaper: {
      width: 280,
      padding: 6,
      borderRadius: 12,
      backgroundColor: theme.palette.aeros.surface2 || theme.palette.background.paper,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.12)"}`,
      boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
      "& .MuiList-root": { padding: 0 },
    },
    menuSection: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      padding: "8px 12px 4px",
    },
    menuItem: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "9px 12px",
      borderRadius: 8,
      fontSize: 13,
      color: theme.palette.text.primary,
      whiteSpace: "normal",
      "&:hover": { backgroundColor: "rgba(160,68,255,0.12)" },
      "& .MuiSvgIcon-root": { fontSize: 17, flex: "none" },
      "&.Mui-disabled": { opacity: 0.55 },
    },
    menuItemDesc: { fontSize: 11, color: theme.palette.text.secondary, display: "block" },
    menuDivider: { height: 1, backgroundColor: isLight ? theme.palette.divider : "rgba(255,255,255,0.07)", margin: "5px 8px" },
    menuItemDanger: { color: errorText, "& .MuiSvgIcon-root": { color: "inherit" } },
    // ---- corpo ----
    pageBody: { flex: 1, padding: "0 28px 24px", overflowY: "auto", ...theme.scrollbarStyles, [theme.breakpoints.down("sm")]: { padding: "0 12px 16px" } },
    // barra de seleção roxa acima da tabela
    bulkBar: {
      margin: "16px 0 0",
      backgroundColor: "rgba(160,68,255,0.1)",
      border: "1px solid rgba(160,68,255,0.3)",
      borderRadius: 12,
      padding: "10px 16px",
      display: "flex",
      alignItems: "center",
      gap: 12,
      flexWrap: "wrap",
    },
    bulkMark: {
      width: 16,
      height: 16,
      borderRadius: 4,
      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
      color: "#07080D",
      fontSize: 11,
      fontWeight: 800,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      flex: "none",
    },
    bulkText: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary },
    bulkActions: { marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" },
    bulkButton: {
      border: `1px solid ${borderStrong}`,
      borderRadius: 9,
      padding: "5px 12px",
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.primary,
      "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 15 },
      "&.Mui-disabled": { opacity: 0.5, color: theme.palette.text.primary },
    },
    bulkDanger: {
      backgroundColor: alpha(AEROS.error, 0.12),
      border: `1px solid ${alpha(AEROS.error, 0.4)}`,
      color: errorText,
      "&:hover": { backgroundColor: AEROS.error, color: "#07080D" },
    },
    // ---- card da tabela em CSS grid ----
    tableCard: {
      marginTop: 14,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      overflow: "hidden",
    },
    // Grid de 8 colunas: abaixo de ~900px os cabecalhos se sobrepunham
    // (WHATSAPP em cima de EMAIL); agora a tabela rola dentro do card.
    tableScroll: { overflowX: "auto", WebkitOverflowScrolling: "touch" },
    tableHeadRow: {
      minWidth: 900,
      display: "grid",
      gridTemplateColumns: TABLE_GRID,
      alignItems: "center",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      backgroundColor: isLight ? "rgba(15,20,40,0.02)" : "rgba(255,255,255,0.02)",
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
    },
    tableRow: {
      minWidth: 900,
      display: "grid",
      gridTemplateColumns: TABLE_GRID,
      alignItems: "center",
      fontSize: 13.5,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
      borderBottom: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"}`,
      "&:hover": { backgroundColor: "rgba(160,68,255,0.04)" },
    },
    tableRowSelected: { backgroundColor: "rgba(160,68,255,0.06)" },
    tableCell: { padding: "12px 8px", minWidth: 0 },
    bodyCell: { padding: "10px 8px", minWidth: 0 },
    checkCell: { paddingLeft: 12, paddingTop: 6, paddingBottom: 6, display: "flex", alignItems: "center" },
    checkbox: { padding: 4, "& .MuiSvgIcon-root": { fontSize: 20 } },
    cellActionsHead: { textAlign: "right", paddingRight: 20 },
    cellActions: { display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 6, paddingRight: 20 },
    nameCellInner: { display: "flex", alignItems: "center", gap: 10 },
    nameLabel: { fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    nameSub: { fontSize: 10.5, color: theme.palette.aeros.textTertiary, lineHeight: 1.3 },
    contactAvatar: {
      width: 32,
      height: 32,
      fontSize: 12,
      fontWeight: 700,
      flexShrink: 0,
      cursor: "pointer",
      transition: "transform 0.2s ease-in-out",
      "&:hover": { transform: "scale(1.1)" },
    },
    secondaryCell: { color: theme.palette.text.secondary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    walletCell: { color: theme.palette.text.secondary, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    tagsCell: { display: "flex", gap: 4, flexWrap: "wrap" },
    tagChip: { fontSize: 10, fontWeight: 600, borderRadius: 5, padding: "1px 7px", whiteSpace: "nowrap" },
    statusPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 99,
      padding: "3px 10px",
      whiteSpace: "nowrap",
    },
    statusDot: { width: 6, height: 6, flexShrink: 0, borderRadius: "50%", backgroundColor: "currentColor" },
    pillActive: { color: stateText(theme, AEROS.success, "success"), backgroundColor: alpha(AEROS.success, 0.1) },
    pillBlocked: { color: errorText, backgroundColor: alpha(AEROS.error, 0.1) },
    rowActionButton: {
      width: 30,
      height: 30,
      borderRadius: 8,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      color: theme.palette.text.secondary,
      padding: 0,
      "&:hover": { color: cyanText, borderColor: alpha(AEROS.cyan, 0.4), backgroundColor: "transparent" },
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    rowActionButtonDelete: {
      "&:hover": { color: errorText, borderColor: alpha(AEROS.error, 0.4), backgroundColor: "transparent" },
    },
    tableFooter: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "12px 20px",
      fontSize: 12,
      color: theme.palette.text.secondary,
    },
    pager: { marginLeft: "auto", display: "flex", gap: 4 },
    pageButton: {
      width: 28,
      height: 28,
      minWidth: 28,
      padding: 0,
      borderRadius: 8,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.1)"}`,
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.primary,
      "& .MuiSvgIcon-root": { fontSize: 16 },
      "&.Mui-disabled": { opacity: 0.4 },
    },
    pageButtonActive: { backgroundColor: "rgba(160,68,255,0.2)", color: purpleText, borderColor: "transparent", fontWeight: 700 },
    emptyRow: { padding: "36px 0", textAlign: "center", fontSize: 12.5, color: theme.palette.aeros.textTertiary },
    // ---- modal da foto ----
    imageDialog: { "& .MuiDialog-paper": { maxWidth: "500px", maxHeight: "500px" } },
    dialogTitle: { display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: theme.spacing(1) },
    profileImage: { width: "100%", height: "auto", maxWidth: "400px", maxHeight: "400px", objectFit: "contain" },
  };
});

// Número exibido na coluna WhatsApp — grupos mostram o id cru; com LGPD
// (lgpdHideNumber) o atendente comum vê o meio do número mascarado.
const formatContactNumber = (contact, masked) => {
  if (!contact) return "";
  if (contact.isGroup) return contact.number;
  const formatted = formatSerializedId(contact.number);
  if (!masked) return formatted;
  return maskPhone(formatted, contact.number);
};

// O backend grava "nopicture.png" como foto de quem não tem — para o avatar
// isso conta como sem foto (mostra as iniciais).
const pictureOf = (contact) => {
  const url = contact?.urlPicture;
  if (!url || /nopicture\.png$/i.test(url)) return undefined;
  return url;
};

const Contacts = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { user, socket } = useContext(AuthContext);
  const { setCurrentTicket } = useContext(TicketsContext);
  const { getAll: getAllSettings } = useCompanySettings();

  const [contacts, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [totalContactsCount, setTotalContactsCount] = useState(0);
  // ?tag=<id> (vindo do modal "Contatos da tag") já entra filtrado.
  const [selectedTags, setSelectedTags] = useState(() => {
    const t = Number(new URLSearchParams(window.location.search).get("tag"));
    return t > 0 ? [t] : [];
  });
  const [tagsAnchorEl, setTagsAnchorEl] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  const [selectedContacts, setSelectedContacts] = useState(new Set());
  const [selectAllMode, setSelectAllMode] = useState(false); // true = todos da empresa

  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [enviarSmsModalOpen, setEnviarSmsModalOpen] = useState(false);
  const [smsContact, setSmsContact] = useState(null);
  const [importSheetOpen, setImportSheetOpen] = useState(false);
  const [importPhoneOpen, setImportPhoneOpen] = useState(false);
  const [importPhoneMode, setImportPhoneMode] = useState("agenda");
  const [bulkTagAnchor, setBulkTagAnchor] = useState(null);
  const [bulkWalletAnchor, setBulkWalletAnchor] = useState(null);
  // { type: "delete" | "block" | "unblock" | "bulk" | "all", contact? }
  const [confirm, setConfirm] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [exporting, setExporting] = useState(false);
  // tag/carteira em lote (desta ou de outra aba) refazem a página atual
  const [reloadTick, setReloadTick] = useState(0);

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedContactName, setSelectedContactName] = useState("");

  const [hideNum, setHideNum] = useState(false);
  const [enableLGPD, setEnableLGPD] = useState(false);
  // Preenchido pelo import-finish/import-cancel da planilha: quais linhas ja
  // existiam no cadastro (o resumo so trazia a contagem).
  const [linhasIgnoradas, setLinhasIgnoradas] = useState([]);

  useEffect(() => {
    async function fetchData() {
      const settingList = await getAllSettings(user.companyId);
      for (const [key, value] of Object.entries(settingList)) {
        if (key === "enableLGPD") setEnableLGPD(value === "enabled");
        if (key === "lgpdHideNumber") setHideNum(value === "enabled");
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A rota antiga /contacts/import redireciona para cá com ?import=1.
  useEffect(() => {
    if (new URLSearchParams(location.search).get("import")) {
      setImportSheetOpen(true);
      history.replace("/contacts");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, history]);

  // ---- seleção múltipla ----
  const handleSelectContact = (contactId) => {
    const next = new Set(selectedContacts);
    if (next.has(contactId)) next.delete(contactId);
    else next.add(contactId);
    setSelectedContacts(next);
    setSelectAllMode(false);
  };

  const clearSelection = () => {
    setSelectedContacts(new Set());
    setSelectAllMode(false);
  };

  const handleSelectAllContacts = () => {
    if (selectAllMode) return clearSelection();
    const pageIds = contacts.map((c) => c.id);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedContacts.has(id));
    if (allPageSelected && selectedContacts.size === pageIds.length) {
      // segundo clique com a página toda marcada = todos da empresa
      setSelectAllMode(true);
      setSelectedContacts(new Set());
    } else {
      setSelectedContacts(new Set(pageIds));
      setSelectAllMode(false);
    }
  };

  useEffect(() => {
    clearSelection();
    setPage(1);
  }, [searchParam, selectedTags]);

  const selectedCount = selectAllMode ? totalContactsCount : selectedContacts.size;
  const isAnyContactSelected = selectAllMode || selectedContacts.size > 0;

  const checkboxStatus = useMemo(() => {
    if (selectAllMode) return { checked: true, indeterminate: false };
    const pageIds = contacts.map((c) => c.id);
    const inPage = pageIds.filter((id) => selectedContacts.has(id)).length;
    if (inPage === 0) return { checked: false, indeterminate: false };
    if (inPage === pageIds.length) return { checked: true, indeterminate: false };
    return { checked: false, indeterminate: true };
  }, [contacts, selectedContacts, selectAllMode]);

  // ---- listagem paginada ----
  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/contacts/", {
            params: { searchParam, pageNumber: page, pageSize: PAGE_SIZE, contactTag: JSON.stringify(selectedTags) },
          });
          dispatch({ type: "RESET" });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setTotalContactsCount(data.count);
        } catch (err) {
          toastError(err);
        } finally {
          setLoading(false);
        }
      };
      fetchContacts();
    }, 400);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, page, selectedTags, reloadTick]);

  const reload = () => {
    setSearchParam("");
    setPage(1);
    // mesma página/busca: o efeito não dispara, então refaz o fetch na mão
    api
      .get("/contacts/", { params: { searchParam: "", pageNumber: 1, pageSize: PAGE_SIZE, contactTag: JSON.stringify(selectedTags) } })
      .then(({ data }) => {
        dispatch({ type: "RESET" });
        dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
        setTotalContactsCount(data.count);
      })
      .catch((err) => toastError(err));
  };

  useEffect(() => {
    const companyId = user.companyId;
    const onContactEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
        if (data.action === "create") setTotalContactsCount((prev) => prev + 1);
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
        setTotalContactsCount((prev) => Math.max(0, prev - 1));
      }
      if (data.action === "bulk-delete") {
        dispatch({ type: "BULK_DELETE_CONTACTS", payload: data.contactIds });
        setTotalContactsCount((prev) => Math.max(0, prev - data.contactIds.length));
      }
      if (data.action === "delete-all") {
        dispatch({ type: "DELETE_ALL_CONTACTS", payload: { excludeIds: data.excludeIds } });
        setTotalContactsCount(data.excludeIds.length);
      }
      if (data.action === "bulk-update") setReloadTick((t) => t + 1);
    };
    socket.on(`company-${companyId}-contact`, onContactEvent);
    return () => {
      socket.off(`company-${companyId}-contact`, onContactEvent);
    };
  }, [socket, user.companyId]);

  // ---- ações ----
  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      const { id, uuid } = ticket;
      setCurrentTicket({ id, uuid, code: uuidv4() });
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleSearch = (event) => setSearchParam(event.target.value.toLowerCase());

  const handleOpenImageModal = (imageUrl, contactName) => {
    setSelectedImage(imageUrl);
    setSelectedContactName(contactName);
    setImageModalOpen(true);
  };

  const handleConfirm = async () => {
    if (!confirm) return;
    setConfirming(true);
    try {
      if (confirm.type === "delete") {
        await api.delete(`/contacts/${confirm.contact.id}`);
        toast.success(i18n.t("contacts.toasts.deleted"));
      } else if (confirm.type === "block" || confirm.type === "unblock") {
        await api.put(`/contacts/block/${confirm.contact.id}`, { active: confirm.type === "unblock" });
        toast.success(i18n.t(confirm.type === "block" ? "contacts.toasts.blocked" : "contacts.toasts.unblocked"));
      } else if (confirm.type === "all") {
        await api.delete("/contacts/all", { data: { confirmation: "DELETE_ALL_CONTACTS", excludeIds: [] } });
        toast.success(i18n.t("aeros.contacts.deletedAll", { count: totalContactsCount }));
        clearSelection();
      } else if (confirm.type === "bulk") {
        const contactIds = Array.from(selectedContacts);
        await api.post("/contacts/bulk-delete", { contactIds });
        toast.success(i18n.t("aeros.contacts.deleted", { count: contactIds.length }));
        clearSelection();
      }
      setConfirm(null);
      reload();
    } catch (err) {
      toastError(err);
    } finally {
      setConfirming(false);
    }
  };

  const handleBulkDeleteClick = () => setConfirm({ type: selectAllMode ? "all" : "bulk" });

  // Exporta todos os contatos do filtro atual em XLSX (mesmas colunas do modelo).
  const handleExport = async () => {
    setExporting(true);
    setMenuAnchorEl(null);
    const masked = enableLGPD && hideNum && user.profile === "user";
    try {
      const rowsOut = [];
      let pageNumber = 1;
      let hasMore = true;
      while (hasMore) {
        const { data } = await api.get("/contacts/", {
          params: { searchParam, pageNumber, contactTag: JSON.stringify(selectedTags) },
        });
        data.contacts.forEach((c) => rowsOut.push(exportRow(c, { masked })));
        hasMore = data.hasMore && data.contacts.length > 0;
        pageNumber += 1;
      }
      const wb = xlsxUtils.book_new();
      xlsxUtils.book_append_sheet(wb, xlsxUtils.json_to_sheet(rowsOut), "Contatos");
      xlsxWriteFile(wb, "contatos.xlsx");
    } catch (err) {
      toastError(err);
    } finally {
      setExporting(false);
    }
  };

  const confirmProps = (() => {
    if (!confirm) return { open: false };
    const c = confirm.contact;
    switch (confirm.type) {
      case "block":
        return {
          icon: <BlockIcon />,
          tone: "danger",
          title: i18n.t("contacts.confirm.blockTitle", { name: c.name }),
          description: i18n.t("contacts.confirm.blockText"),
          confirmLabel: i18n.t("contacts.confirm.block"),
        };
      case "unblock":
        return {
          icon: <CheckCircleOutlineIcon />,
          tone: "info",
          primary: true,
          title: i18n.t("contacts.confirm.unblockTitle", { name: c.name }),
          description: i18n.t("contacts.confirm.unblockText"),
          confirmLabel: i18n.t("contacts.confirm.unblock"),
        };
      case "delete":
        return {
          icon: <DeleteForeverIcon />,
          title: i18n.t("contacts.confirmDelete.oneTitle", { name: c.name }),
          description: i18n.t("contacts.confirmDelete.text"),
          confirmLabel: i18n.t("contacts.confirmDelete.confirm"),
        };
      case "bulk":
        return {
          icon: <DeleteForeverIcon />,
          title: i18n.t("contacts.confirmDelete.manyTitle", { count: selectedContacts.size }),
          description: i18n.t("contacts.confirmDelete.text"),
          confirmLabel: i18n.t("contacts.confirmDelete.confirm"),
          typedWord: "EXCLUIR",
        };
      default:
        return {
          icon: <DeleteForeverIcon />,
          title: i18n.t("contacts.confirmDelete.allTitle", { count: totalContactsCount }),
          description: i18n.t("contacts.confirmDelete.text"),
          confirmLabel: i18n.t("contacts.confirmDelete.confirm"),
          typedWord: "EXCLUIR",
        };
    }
  })();

  const pages = totalPages(totalContactsCount, PAGE_SIZE);
  const menuOpen = Boolean(menuAnchorEl);

  return (
    <MainContainer className={classes.mainContainer}>
      <NewTicketModal modalOpen={newTicketModalOpen} initialContact={contactTicket} onClose={handleCloseOrOpenTicket} />
      <ContactModal
        open={contactModalOpen}
        onClose={() => {
          setSelectedContactId(null);
          setContactModalOpen(false);
        }}
        contactId={selectedContactId}
      />
      <EnviarSmsModal
        open={enviarSmsModalOpen}
        onClose={() => {
          setEnviarSmsModalOpen(false);
          setSmsContact(null);
        }}
        contact={smsContact}
      />
      <ContactImportModal open={importSheetOpen} onClose={() => setImportSheetOpen(false)} onStarted={() => setLinhasIgnoradas([])} />
      <ContactImportPhoneModal open={importPhoneOpen} mode={importPhoneMode} onClose={() => setImportPhoneOpen(false)} />
      <BulkTagPopover
        anchorEl={bulkTagAnchor}
        onClose={() => setBulkTagAnchor(null)}
        selection={{ all: selectAllMode, ids: selectedContacts }}
        onDone={() => setReloadTick((t) => t + 1)}
      />
      <BulkWalletPopover
        anchorEl={bulkWalletAnchor}
        onClose={() => setBulkWalletAnchor(null)}
        selection={{ all: selectAllMode, ids: selectedContacts }}
        onDone={() => setReloadTick((t) => t + 1)}
      />
      <AerosConfirmModal
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        onConfirm={handleConfirm}
        loading={confirming}
        {...confirmProps}
      />

      {/* Modal para visualizar imagem de perfil */}
      <Dialog open={imageModalOpen} onClose={() => setImageModalOpen(false)} className={classes.imageDialog} maxWidth="md">
        <DialogTitle className={classes.dialogTitle}>
          <span>Foto de Perfil - {selectedContactName}</span>
          <IconButton onClick={() => setImageModalOpen(false)} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedImage ? (
            <img
              src={selectedImage}
              alt={`Foto de perfil de ${selectedContactName}`}
              className={classes.profileImage}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          ) : (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: 200, color: theme.palette.text.secondary }}>
              Imagem não disponível
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className={classes.pageHeader}>
        <div>
          <Typography component="div" className={classes.pageTitle}>
            {i18n.t("contacts.title")} <span className={classes.pageTitleCount}>· {totalContactsCount}</span>
          </Typography>
          {isAnyContactSelected && (
            <div className={classes.pageSubtitle}>{i18n.t("contacts.table.selected", { count: selectedCount })}</div>
          )}
        </div>
        <div className={classes.headerActions}>
          <InputBase
            className={classes.searchPill}
            placeholder={i18n.t("contacts.table.search")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            startAdornment={<SearchIcon />}
            inputProps={{ "aria-label": i18n.t("contacts.table.search") }}
          />

          <Button
            variant="outlined"
            className={`${classes.ghostButton} ${selectedTags.length > 0 ? classes.ghostButtonActive : ""}`}
            startIcon={<LocalOfferOutlinedIcon />}
            onClick={(e) => setTagsAnchorEl(e.currentTarget)}
            aria-haspopup="true"
          >
            {i18n.t("contacts.table.tags")}
            {selectedTags.length > 0 && <span className={classes.tagsCount}>{selectedTags.length}</span>}
          </Button>
          <Popover
            open={Boolean(tagsAnchorEl)}
            anchorEl={tagsAnchorEl}
            onClose={() => setTagsAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{ className: classes.tagsPopover }}
          >
            <TagsFilter onFiltered={(selecteds) => setSelectedTags(selecteds.map((t) => t.id))} />
          </Popover>

          <Button
            variant="outlined"
            className={`${classes.ghostButton} ${menuOpen ? classes.ghostButtonActive : ""}`}
            endIcon={menuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={(e) => setMenuAnchorEl(e.currentTarget)}
            aria-haspopup="true"
            disabled={exporting}
          >
            {exporting ? i18n.t("contacts.exporting") : i18n.t("contacts.menu.importexport")}
          </Button>
          <Menu
            open={menuOpen}
            anchorEl={menuAnchorEl}
            onClose={() => setMenuAnchorEl(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            PaperProps={{ className: classes.menuPaper }}
          >
            <div className={classes.menuSection}>{i18n.t("contacts.menu.importSection")}</div>
            <MenuItem
              className={classes.menuItem}
              onClick={() => {
                setMenuAnchorEl(null);
                setImportPhoneMode("agenda");
                setImportPhoneOpen(true);
              }}
            >
              <ContactPhoneOutlinedIcon style={{ color: AEROS.success }} />
              <div>
                {i18n.t("contacts.menu.importYourPhone")}
                <span className={classes.menuItemDesc}>{i18n.t("contacts.menu.importYourPhoneDesc")}</span>
              </div>
            </MenuItem>
            <MenuItem
              className={classes.menuItem}
              onClick={() => {
                setMenuAnchorEl(null);
                setImportSheetOpen(true);
              }}
            >
              <CloudUploadOutlinedIcon style={{ color: "#C08BFF" }} />
              <div>
                {i18n.t("contacts.menu.importToExcel")}
                <span className={classes.menuItemDesc}>{i18n.t("contacts.menu.importToExcelDesc")}</span>
              </div>
            </MenuItem>
            <MenuItem
              className={classes.menuItem}
              onClick={() => {
                setMenuAnchorEl(null);
                setImportPhoneMode("chats");
                setImportPhoneOpen(true);
              }}
            >
              <ForumOutlinedIcon style={{ color: AEROS.cyan }} />
              <div>
                {i18n.t("contacts.menu.importChats")}
                <span className={classes.menuItemDesc}>{i18n.t("contacts.menu.importChatsDesc")}</span>
              </div>
            </MenuItem>
            <div className={classes.menuDivider} />
            <div className={classes.menuSection}>{i18n.t("contacts.menu.exportSection")}</div>
            <MenuItem className={classes.menuItem} onClick={handleExport}>
              <GetAppIcon style={{ color: theme.palette.text.secondary }} />
              <div>
                {i18n.t("contacts.menu.exportSheet")}
                <span className={classes.menuItemDesc}>{i18n.t("contacts.menu.exportSheetDesc", { count: totalContactsCount })}</span>
              </div>
            </MenuItem>
            <Can
              role={user.profile}
              perform="contacts-page:deleteContact"
              yes={() => (
                <div>
                  <div className={classes.menuDivider} />
                  <MenuItem
                    className={`${classes.menuItem} ${classes.menuItemDanger}`}
                    onClick={() => {
                      setMenuAnchorEl(null);
                      setConfirm({ type: "all" });
                    }}
                  >
                    <DeleteSweepIcon />
                    {i18n.t("contacts.menu.deleteAll")}
                  </MenuItem>
                </div>
              )}
            />
          </Menu>

          <Button
            variant="contained"
            color="primary"
            className={classes.gradientButton}
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedContactId(null);
              setContactModalOpen(true);
            }}
          >
            {i18n.t("contacts.buttons.add")}
          </Button>
        </div>
      </div>

      <div className={classes.pageBody}>
        {/* Barras padrao das importacoes em segundo plano: planilha e agenda
            do celular, cada uma no seu canal. A da planilha suporta
            cancelamento cooperativo no backend. */}
        <ImportacaoProgresso
          canal={`company-${user.companyId}-importacao-contatos`}
          onFinish={(data) => {
            if (data.ignorados > 0) setLinhasIgnoradas(data.linhasIgnoradas || []);
          }}
          onCancel={(data) => setLinhasIgnoradas(data.linhasIgnoradas || [])}
          onCancelar={async () => {
            try {
              await api.post("/contacts/import/cancel");
            } catch (err) {
              toastError(err);
            }
          }}
        />
        <ImportacaoProgresso canal={`company-${user.companyId}-importacao-agenda`} />

        {linhasIgnoradas.length > 0 && (
          <Alert severity="info" onClose={() => setLinhasIgnoradas([])} style={{ margin: "16px 0 0" }}>
            <AlertTitle>{linhasIgnoradas.length} contato(s) da planilha ja existiam no cadastro</AlertTitle>
            <div style={{ maxHeight: 160, overflowY: "auto" }}>
              {linhasIgnoradas.map((item) => (
                <Typography variant="caption" component="div" key={item.linha}>
                  Linha {item.linha}
                  {item.nome ? ` (${item.nome})` : ""}: {item.numero}
                </Typography>
              ))}
            </div>
          </Alert>
        )}

        {isAnyContactSelected && (
          <div className={classes.bulkBar}>
            <div className={classes.bulkMark} onClick={clearSelection} role="button" aria-label={i18n.t("aeros.contacts.clearSelection")} title={i18n.t("aeros.contacts.clearSelection")}>
              –
            </div>
            <div className={classes.bulkText}>
              {i18n.t(selectAllMode ? "contacts.bulk.selectedAll" : "contacts.bulk.selected", { count: selectedCount })}
            </div>
            <div className={classes.bulkActions}>
              <Button className={classes.bulkButton} startIcon={<LocalOfferOutlinedIcon />} onClick={(e) => setBulkTagAnchor(e.currentTarget)}>
                {i18n.t("contacts.bulk.addTag")}
              </Button>
              <Button className={classes.bulkButton} startIcon={<PersonPinOutlinedIcon />} onClick={(e) => setBulkWalletAnchor(e.currentTarget)}>
                {i18n.t("contacts.bulk.assignWallet")}
              </Button>
              <Can
                role={user.profile}
                perform="contacts-page:deleteContact"
                yes={() => (
                  <Button className={`${classes.bulkButton} ${classes.bulkDanger}`} startIcon={<DeleteOutlineIcon />} onClick={handleBulkDeleteClick}>
                    {i18n.t("contacts.bulk.deleteSelected")}
                  </Button>
                )}
              />
            </div>
          </div>
        )}

        <div className={classes.tableCard}>
          <div className={classes.tableScroll}>
          <div className={classes.tableHeadRow}>
            <div className={`${classes.tableCell} ${classes.checkCell}`}>
              <Checkbox
                className={classes.checkbox}
                color="primary"
                indeterminate={checkboxStatus.indeterminate}
                checked={checkboxStatus.checked}
                onChange={handleSelectAllContacts}
                disabled={contacts.length === 0}
                inputProps={{ "aria-label": "Selecionar todos os contatos" }}
              />
            </div>
            <div className={classes.tableCell}>{i18n.t("contacts.table.name")}</div>
            <div className={classes.tableCell}>{i18n.t("contacts.table.number")}</div>
            <div className={classes.tableCell}>{i18n.t("contacts.table.email")}</div>
            <div className={classes.tableCell}>{i18n.t("contacts.table.tags")}</div>
            <div className={classes.tableCell}>{i18n.t("contacts.table.status")}</div>
            <div className={classes.tableCell}>{i18n.t("contacts.table.wallet")}</div>
            <div className={`${classes.tableCell} ${classes.cellActionsHead}`}>{i18n.t("contacts.table.actions")}</div>
          </div>

          {contacts.map((contact) => {
            const isSelected = selectAllMode || selectedContacts.has(contact.id);
            const masked = enableLGPD && hideNum && user.profile === "user";
            return (
              <div key={contact.id} className={`${classes.tableRow} ${isSelected ? classes.tableRowSelected : ""}`}>
                <div className={`${classes.bodyCell} ${classes.checkCell}`}>
                  <Checkbox
                    className={classes.checkbox}
                    color="primary"
                    checked={isSelected}
                    onChange={() => handleSelectContact(contact.id)}
                    disabled={selectAllMode}
                    inputProps={{ "aria-label": `Selecionar contato ${contact.name}` }}
                  />
                </div>
                <div className={`${classes.bodyCell} ${classes.nameCellInner}`}>
                  <Avatar
                    src={pictureOf(contact)}
                    alt={contact.name}
                    className={classes.contactAvatar}
                    onClick={() => handleOpenImageModal(pictureOf(contact), contact.name)}
                    style={{ backgroundColor: avatarColorFor(contact.id), color: "#07080D" }}
                  >
                    {initialsOf(contact.name)}
                  </Avatar>
                  <div style={{ minWidth: 0 }}>
                    <div className={classes.nameLabel} title={contact.name}>
                      {contact.name}
                    </div>
                    <div className={classes.nameSub}>#{contact.id}</div>
                  </div>
                </div>
                <div className={`${classes.bodyCell} ${classes.secondaryCell}`}>{formatContactNumber(contact, masked)}</div>
                <div className={`${classes.bodyCell} ${classes.secondaryCell}`} title={contact.email || undefined}>
                  {contact.email || "—"}
                </div>
                <div className={`${classes.bodyCell} ${classes.tagsCell}`}>
                  {(contact.tags || []).map((t) => {
                    const chip = tagChip(t);
                    return (
                      <span key={t.id} className={classes.tagChip} style={{ color: chip.color, backgroundColor: chip.bg }}>
                        {chip.label}
                      </span>
                    );
                  })}
                </div>
                <div className={classes.bodyCell}>
                  <span className={`${classes.statusPill} ${contact.active ? classes.pillActive : classes.pillBlocked}`}>
                    <span className={classes.statusDot} />
                    {contact.active ? "Ativo" : "Bloqueado"}
                  </span>
                </div>
                <div className={`${classes.bodyCell} ${classes.walletCell}`}>{walletNameOf(contact) || "—"}</div>
                <div className={`${classes.bodyCell} ${classes.cellActions}`}>
                  <Tooltip title={i18n.t("aeros.contacts.startTicket")}>
                    <span>
                      <IconButton
                        size="small"
                        className={classes.rowActionButton}
                        disabled={!contact.active}
                        onClick={() => {
                          setContactTicket(contact);
                          setNewTicketModalOpen(true);
                        }}
                        aria-label={i18n.t("aeros.contacts.startTicket")}
                      >
                        {contact.channel === "instagram" ? <Instagram /> : contact.channel === "facebook" ? <Facebook /> : <WhatsApp />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={i18n.t("aeros.contacts.sendSms")}>
                    <IconButton
                      size="small"
                      className={classes.rowActionButton}
                      onClick={() => {
                        setSmsContact(contact);
                        setEnviarSmsModalOpen(true);
                      }}
                      aria-label={i18n.t("aeros.contacts.sendSms")}
                    >
                      <Sms />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t("aeros.contacts.edit")}>
                    <IconButton
                      size="small"
                      className={classes.rowActionButton}
                      onClick={() => {
                        setSelectedContactId(contact.id);
                        setContactModalOpen(true);
                      }}
                      aria-label={i18n.t("aeros.contacts.editContact")}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={contact.active ? "Bloquear" : "Desbloquear"}>
                    <IconButton
                      size="small"
                      className={classes.rowActionButton}
                      onClick={() => setConfirm({ type: contact.active ? "block" : "unblock", contact })}
                      aria-label={contact.active ? "Bloquear contato" : "Desbloquear contato"}
                    >
                      {contact.active ? <BlockIcon /> : <CheckCircleIcon />}
                    </IconButton>
                  </Tooltip>
                  <Can
                    role={user.profile}
                    perform="contacts-page:deleteContact"
                    yes={() => (
                      <Tooltip title={i18n.t("aeros.contacts.delete")}>
                        <IconButton
                          size="small"
                          className={`${classes.rowActionButton} ${classes.rowActionButtonDelete}`}
                          onClick={() => setConfirm({ type: "delete", contact })}
                          aria-label={i18n.t("aeros.contacts.deleteContact")}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  />
                </div>
              </div>
            );
          })}

          {loading && (
            <div className={classes.emptyRow}>
              <CircularProgress size={22} />
            </div>
          )}
          {!loading && contacts.length === 0 && <div className={classes.emptyRow}>{i18n.t("aeros.contacts.notFound")}</div>}

          <div className={classes.tableFooter}>
            {i18n.t("contacts.pagination.showing", { shown: contacts.length, total: totalContactsCount })}
            {pages > 1 && (
              <div className={classes.pager}>
                <IconButton className={classes.pageButton} disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label={i18n.t("contacts.pagination.prev")}>
                  <ChevronLeftIcon />
                </IconButton>
                {pageWindow(page, pages).map((p) => (
                  <Button key={p} className={`${classes.pageButton} ${p === page ? classes.pageButtonActive : ""}`} onClick={() => setPage(p)} aria-current={p === page ? "page" : undefined}>
                    {p}
                  </Button>
                ))}
                <IconButton className={classes.pageButton} disabled={page >= pages} onClick={() => setPage(page + 1)} aria-label={i18n.t("contacts.pagination.next")}>
                  <ChevronRightIcon />
                </IconButton>
              </div>
            )}
          </div>
        </div>
          </div>
      </div>
    </MainContainer>
  );
};

export default Contacts;
